import { createCommand, Option } from '@commander-js/extra-typings';
import { NO_TTY_JSON_OUTPUT_OPTION } from '@wix/cli-command-contract';
import type { AstroCommandServices } from '@wix/cli-command-contract';
import React from 'react';
import { validateDocsExtensionType, validateValidJson } from './validations.js';
import { generateManifestCommand } from './generate-manifest.js';
import { log } from '../non-interactive/logger.js';

export const generateCommand = (
  services: AstroCommandServices,
  cliVersion: string
) => {
  return createCommand('generate')
    .description('Generate a new extension for your project')
    .addCommand(generateManifestCommand(services, cliVersion), { hidden: true })
    .addOption(
      new Option('--type <type>', 'Extension type to generate')
        .argParser(validateDocsExtensionType)
        .conflicts('params')
    )
    .addOption(
      new Option(
        '--params <json>',
        'Generate an extension non-interactively from a JSON object'
      )
        .conflicts('type')
        .implies({ json: true })
        .argParser(validateValidJson)
    )
    .addOption(
      new Option(NO_TTY_JSON_OUTPUT_OPTION, 'Output in JSON format').hideHelp()
    )
    .addOption(
      new Option(
        '--experimental',
        'Include experimental generators in the picker'
      ).hideHelp()
    )
    .action(async (options, command) => {
      const { type, params } = options;
      const { runCommand, renderCommand } = await import('./render-command.js');

      if (params !== undefined) {
        await runCommand(command, services, cliVersion, async (model) => {
          const { nonInteractiveGenerateExtension } = await import(
            '../non-interactive/generate-extensions.js'
          );

          try {
            const result = await nonInteractiveGenerateExtension(
              services,
              model,
              params
            );

            log(JSON.stringify({ success: true, ...result }));
          } catch (e) {
            log(
              JSON.stringify({
                success: false,
                error: e instanceof Error ? e.message : String(e),
              })
            );
            throw e;
          }
        });
      } else {
        const { GenerateCommand } = await import(
          '../components/GenerateCommand/index.js'
        );

        await renderCommand(
          command,
          services,
          cliVersion,
          <GenerateCommand docsExtensionType={type} />
        );
      }
    });
};
